import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { parseDateSafariCompatible } from '../../utils/dateDebugUtils';
import TableSortLabel from '@mui/material/TableSortLabel';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import { useEvents } from '../../contexts/EventsContext';
import { useUser } from '../../contexts/UserContext';
import { useSchools } from '../../contexts/SchoolsContext';
import { EventListItem } from '../../types';
import { activityLogService } from '../../services/activityLogService';
import CreateEventModal from './CreateEventModal';

interface UserFavorite {
  name: string;
  type: 'school' | 'program';
  schoolName?: string;
  sortOrder: number;
}

interface EventsListProps {
  filterMode?: 'favorites' | 'all';
  userFavorites?: UserFavorite[];
  userHighSchool?: string;
  onFilterChange?: (event: React.MouseEvent<HTMLElement>, newFilter: 'favorites' | 'all' | null) => void;
}

const EventsList: React.FC<EventsListProps> = ({ 
  filterMode = 'all', 
  userFavorites = [], 
  userHighSchool = '',
  onFilterChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { events, loading, error, refresh } = useEvents();
  const { user } = useUser();
  const { schools } = useSchools();
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false);

  // Helper function to get the correct spelling based on user's country
  const getFavouritesLabel = () => {
    return user?.country === 'US' ? 'Favorites' : 'Favourites';
  };
  const [hostSearch, setHostSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [dateSearch, setDateSearch] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    // Default to card view on mobile, list view on desktop
    return isMobile ? 'card' : 'list';
  });
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'date', direction: 'asc' });

  // Update view mode when mobile state changes
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'list');
  }, [isMobile]);

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const EVENTS_PER_PAGE = 30;

  // Map events to EventListItem shape
  const mappedEvents: EventListItem[] = events.map(event => {
    let dateStr = '';
    let timeStr = '';
    let isoDate = '';
    if ((event as any).startTime) {
      let startTimeStr = (event as any).startTime;
      if (typeof startTimeStr === 'string') {
        startTimeStr = startTimeStr.replace(' ', 'T');
      }
      const dateObj = new Date(startTimeStr);
      if (!isNaN(dateObj.getTime())) {
        dateStr = dateObj.toLocaleDateString();
        timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        isoDate = dateObj.toISOString();
      } else {
        dateStr = '';
        timeStr = '';
        isoDate = '';
      }
    }
    
    // Handle eventCategory with backward compatibility (support both string and array)
    let eventCategory = (event as any).eventCategory;
    if (eventCategory && !Array.isArray(eventCategory)) {
      eventCategory = [eventCategory];
    } else if (!eventCategory) {
      eventCategory = ['General'];
    }

    return {
    id: event.id,
    host: (event as any).hostName || '',
    name: (event as any).title || '',
      date: dateStr,
      isoDate: isoDate,
      time: timeStr,
    location: (event as any).address || '',
    description: (event as any).description || '',
    private: (event as any).private,
    endTime: (event as any).endTime || '',
    eventType: (event as any).eventType || '',
    registerRequired: (event as any).registerRequired,
    website: (event as any).website || '',
    meetingID: (event as any).meetingID || '',
    meetingPassword: (event as any).meetingPassword || '',
    isPostToAll: (event as any).isPostToAll,
    hasTarget: (event as any).hasTarget,
    eventTargetStr: (event as any).eventTargetStr || '',
    activityID: (event as any).activityID || '',
      eventCategory: eventCategory,
      schoolId: (event as any).schoolId || '',
      userId: (event as any).userId || '',
    };
  });

  // Debug: log the first 5 mapped events
  if (mappedEvents.length > 0) {
    // eslint-disable-next-line no-console
    console.log('Mapped events for table:', mappedEvents.slice(0, 5));
  }

  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  // Create a set of favorite school names for efficient lookup (case-insensitive)
  const favoriteSchoolNames = useMemo(() => {
    const schoolNames = new Set<string>();
    
    // Add favorite schools
    userFavorites
      .filter(fav => fav.type === 'school')
      .forEach(fav => schoolNames.add(fav.name.toLowerCase()));
    
    // Add schools of favorite programs
    userFavorites
      .filter(fav => fav.type === 'program' && fav.schoolName)
      .forEach(fav => schoolNames.add(fav.schoolName!.toLowerCase()));
    
    // Add Fosterway (case-insensitive)
    schoolNames.add('fosterway');
    schoolNames.add('fosterway university');
    
    // Add user's high school
    if (userHighSchool) {
      schoolNames.add(userHighSchool.toLowerCase());
    }
    
    return schoolNames;
  }, [userFavorites, userHighSchool]);

  // Get all unique event categories from events
  const uniqueCategories = useMemo(() => {
    const categorySet = new Set<string>();
    mappedEvents.forEach(event => {
      if (event.eventCategory) {
        const categories = Array.isArray(event.eventCategory) 
          ? event.eventCategory 
          : [event.eventCategory];
        categories.forEach(cat => {
          if (cat && cat.trim() !== '') {
            categorySet.add(cat);
          }
        });
      }
    });
    return Array.from(categorySet).sort();
  }, [mappedEvents]);

  const filteredEvents = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
    
    return mappedEvents.filter((event) => {
      const hostMatch = (event.host || '').toLowerCase().includes(hostSearch.toLowerCase());
      const nameMatch = (event.name || '').toLowerCase().includes(nameSearch.toLowerCase());
      const dateMatch = (event.date || '').toLowerCase().includes(dateSearch.toLowerCase());
      
      // Parse event date using the same logic as Dashboard
      const eventDate = new Date(event.date);
      // Only include events >= today (future events)
      const isFutureEvent = !isNaN(eventDate.getTime()) && eventDate >= today;
      
      // Apply favorites filter if enabled (case-insensitive comparison)
      const favoritesMatch = filterMode === 'all' || favoriteSchoolNames.has(event.host.toLowerCase());
      
      // Apply category filter - event must contain at least one of the selected categories
      const categoryMatch = categoryFilter.length === 0 || (() => {
        const eventCategories = event.eventCategory 
          ? (Array.isArray(event.eventCategory) ? event.eventCategory : [event.eventCategory])
          : ['General'];
        return categoryFilter.some(selectedCat => eventCategories.includes(selectedCat));
      })();
      
      return hostMatch && nameMatch && dateMatch && isFutureEvent && favoritesMatch && categoryMatch;
    });
      }, [mappedEvents, hostSearch, nameSearch, dateSearch, categoryFilter, filterMode, favoriteSchoolNames]);

  const sortedEvents = useMemo(() => {
    const arr = [...filteredEvents];
    arr.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      switch (sortConfig.key) {
        case 'host':
          aValue = a.host.toLowerCase();
          bValue = b.host.toLowerCase();
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'date':
          aValue = a.isoDate || '';
          bValue = b.isoDate || '';
          if (sortConfig.direction === 'asc') {
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
            return 0;
          } else {
            if (aValue < bValue) return 1;
            if (aValue > bValue) return -1;
            return 0;
          }

        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredEvents, sortConfig]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [hostSearch, nameSearch, dateSearch, categoryFilter, viewMode, filterMode]);

  const paginatedEvents = React.useMemo(() => {
    const startIdx = (page - 1) * EVENTS_PER_PAGE;
    return sortedEvents.slice(startIdx, startIdx + EVENTS_PER_PAGE);
  }, [sortedEvents, page]);
  const pageCount = Math.ceil(sortedEvents.length / EVENTS_PER_PAGE);

  // Debug: log pageCount and sortedEvents.length
  // eslint-disable-next-line no-console
  console.log('pageCount:', pageCount, 'sortedEvents.length:', sortedEvents.length);

  // Handle event website click
  const handleEventWebsiteClick = async (event: React.MouseEvent, eventId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (eventId && user?.uid) {
      // Log the external event website referral activity
      try {
        await activityLogService.logExternalEventWebsiteReferral(user.uid, eventId);
      } catch (error) {
        console.error('Error logging external event website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Find the event to get the website URL
      const eventData = events.find(e => e.id === eventId);
      if (eventData?.website) {
        window.open(eventData.website, '_blank');
      }
    }
  };

  // Helper function to format date and time for display
  const formatEventDateTime = (dateStr: string, timeStr: string): string => {
    if (!dateStr) return 'N/A';
    
    try {
      // FIX: Use Safari-compatible date parsing
      const date = parseDateSafariCompatible(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format date as "mmm dd, yyyy"
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      
      const formattedDate = `${month} ${day}, ${year}`;
      
      // Format time
      let formattedTime = '';
      if (timeStr) {
        // Handle time strings like "08:00 PM" or "11:00 AM"
        const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          const period = timeMatch[3].toLowerCase();
          
          // Convert to 12-hour format without leading zero
          if (period === 'pm' && hours !== 12) {
            hours += 12;
          } else if (period === 'am' && hours === 12) {
            hours = 0;
          }
          
          const displayHours = hours % 12 || 12;
          const ampm = hours >= 12 ? 'p' : 'a';
          
          formattedTime = `${displayHours}:${minutes}${ampm}`;
        } else {
          // Fallback for other time formats - use Safari-compatible parsing
          const timeDate = parseDateSafariCompatible(timeStr);
          const hours = timeDate.getHours();
          const minutes = timeDate.getMinutes();
          const ampm = hours >= 12 ? 'p' : 'a';
          const displayHours = hours % 12 || 12;
          formattedTime = `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm}`;
        }
      }
      
      return formattedTime ? `${formattedDate} (${formattedTime})` : formattedDate;
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper to truncate description to 50 words
  const truncateDescription = (description: string) => {
    if (!description) return '';
    const words = description.split(' ');
    if (words.length <= 50) return description;
    return words.slice(0, 50).join(' ') + '...';
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Box sx={{ 
        width: '100%',
        px: 0.5,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Sticky Search Bar */}
        <Box sx={{ 
          position: 'sticky',
          top: { xs: '67px', sm: '77px' }, // Responsive positioning: mobile 67px, desktop 77px
          zIndex: 1000,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
          mb: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(8px)',
          // Ensure complete background coverage
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'background.paper',
            zIndex: -1
          },
          // Additional background insurance
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'background.paper',
            zIndex: -2
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by event host..."
              value={hostSearch}
              onChange={(e) => setHostSearch(e.target.value)}
              sx={{
                backgroundColor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by event name..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              sx={{
                backgroundColor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth sx={{
              backgroundColor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              }
            }}>
              <InputLabel>Search by Category</InputLabel>
              <Select
                multiple
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                label="Search by Category"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <Typography variant="body2" color="text.secondary">All Categories</Typography>;
                  }
                  if (selected.length === 1) {
                    return selected[0];
                  }
                  return `${selected.length} categories selected`;
                }}
              >
                {uniqueCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    <Checkbox checked={categoryFilter.includes(category)} />
                    <ListItemText primary={category} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by date..."
              value={dateSearch}
              onChange={(e) => setDateSearch(e.target.value)}
              sx={{
                backgroundColor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

          </Box>
        </Box>

        {/* Scrollable Content Area */}
        <Box sx={{ 
          flex: 1,
          overflowY: 'auto',
          px: 0.5
        }}>
          {/* Results Count, Filter Toggle, and View Toggle */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 0.5, 
            gap: { xs: 1, sm: 2 }
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              width: { xs: '100%', sm: 'auto' },
              mb: { xs: 1, sm: 0 }
            }}>
              <Typography variant="subtitle1">
                {sortedEvents.length} events displayed
                {filterMode === 'favorites' && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    (filtered to show only favorites)
                  </Typography>
                )}
              </Typography>
            </Box>
            
            {/* Filter Toggle - Centered */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: { xs: 'flex-start', sm: 'center' }, 
              width: { xs: '100%', sm: 'auto' },
              flex: { xs: 'none', sm: 1 }
            }}>
              <ToggleButtonGroup
                value={filterMode}
                exclusive
                onChange={onFilterChange}
                aria-label="event filter"
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                <Tooltip title={`Show events hosted by schools in my ${getFavouritesLabel().toLowerCase()}, my high school, and Fosterway`} arrow>
                  <ToggleButton value="favorites" aria-label={`only show ${getFavouritesLabel().toLowerCase()}`}>
                    Only Show {getFavouritesLabel()}
                  </ToggleButton>
                </Tooltip>
                <ToggleButton value="all" aria-label="show all events">
                  Show All Events
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* View Toggle and Create New Event Button - Right aligned */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: { xs: 'flex-end', sm: 'flex-end' },
              flex: { xs: 'none', sm: 1 },
              gap: 2
            }}>
              {/* Create New Event Button - Only for Guidance Users */}
              {user?.isGuidanceUser && (
                <Button
                  variant="contained"
                  onClick={() => setCreateEventModalOpen(true)}
                  sx={{
                    backgroundColor: '#8dd439', // Fosterway green
                    color: '#000000', // Black font
                    '&:hover': {
                      backgroundColor: '#7fc400', // Slightly darker green on hover
                    },
                  }}
                >
                  + Create New Event
                </Button>
              )}
              <IconButton
                aria-label="List view"
                color={viewMode === 'list' ? 'primary' : 'default'}
                onClick={() => setViewMode('list')}
                size="large"
              >
                <ViewListIcon />
              </IconButton>
              <IconButton
                aria-label="Card view"
                color={viewMode === 'card' ? 'primary' : 'default'}
                onClick={() => setViewMode('card')}
                size="large"
              >
                <ViewModuleIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Events Display */}
          {viewMode === 'card' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {paginatedEvents.map((event) => (
                <Box key={event.id} sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, bgcolor: 'background.paper', boxShadow: 1, cursor: 'pointer' }}
                  onClick={() => navigate(`/events/${event.id}`, { state: { event } })}
                >
                  <Typography variant="h6" gutterBottom>{event.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {(() => {
                      if (event.host === user?.schoolName && user?.userIcon) {
                        return (
                          <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 1 }}>
                            <img src={user.userIcon} alt={user.schoolName} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                          </Avatar>
                        );
                      }
                      const school = schools.find(s => s.name === event.host);
                      if (school && (school.feedIcon || school.resultsImage)) {
                        return (
                          <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 1 }}>
                            <img src={school.feedIcon || school.resultsImage} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                          </Avatar>
                        );
                      }
                      // Fallback for Fosterway events
                      if (event.host.toLowerCase().includes('fosterway')) {
                        return (
                          <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 1 }}>
                            <img src="https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd" alt="Fosterway University" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                          </Avatar>
                        );
                      }
                      return null;
                    })()}
                    <Typography variant="subtitle2" color="text.secondary">
                      {event.host}
                      {event.private && (
                        <LockOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                      )}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Date: {formatEventDateTime(event.date, event.time || '')}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Location: {event.location}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>{truncateDescription(event.description)}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => handleSort('host')}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 32, height: 32, mr: 2 }}></Box>
                        <TableSortLabel
                          active={sortConfig.key === 'host'}
                          direction={sortConfig.key === 'host' ? sortConfig.direction : 'asc'}
                        >
                          Event Host
                        </TableSortLabel>
                      </Box>
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      <TableSortLabel
                        active={sortConfig.key === 'name'}
                        direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                      >
                        Event Name
                      </TableSortLabel>
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => handleSort('location')}>
                      <TableSortLabel
                        active={sortConfig.key === 'location'}
                        direction={sortConfig.key === 'location' ? sortConfig.direction : 'asc'}
                      >
                        Event Location
                      </TableSortLabel>
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'middle', cursor: 'pointer', minWidth: 150 }} onClick={() => handleSort('date')}>
                      <TableSortLabel
                        active={sortConfig.key === 'date'}
                        direction={sortConfig.key === 'date' ? sortConfig.direction : 'asc'}
                      >
                        Event Date
                      </TableSortLabel>
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'center', verticalAlign: 'middle' }}>
                      {/* Website column header intentionally left blank */}
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {paginatedEvents.map((event) => (
                    <Box
                      component="tr"
                      key={event.id}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'grey.50' },
                        borderBottom: 1,
                        borderColor: 'divider'
                      }}
                      onClick={() => navigate(`/events/${event.id}`, { state: { event } })}
                    >
                      <Box component="td" sx={{ p: 1, display: 'flex', alignItems: 'center', verticalAlign: 'middle' }}>
                        {(() => {
                          if (event.host === user?.schoolName && user?.userIcon) {
                            return (
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                                <img src={user.userIcon} alt={user.schoolName} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                              </Avatar>
                            );
                          }
                          const school = schools.find(s => s.name === event.host);
                          if (school && (school.feedIcon || school.resultsImage)) {
                            return (
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                                <img src={school.feedIcon || school.resultsImage} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                              </Avatar>
                            );
                          }
                          // Fallback for Fosterway events
                          if (event.host.toLowerCase().includes('fosterway')) {
                            return (
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                                <img src="https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd" alt="Fosterway University" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} />
                              </Avatar>
                            );
                          }
                          return null;
                        })()}
                        {event.host}
                        {event.private && (
                          <LockOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        )}
                      </Box>
                      <Box component="td" sx={{ p: 1, verticalAlign: 'middle' }}>{event.name}</Box>
                      <Box component="td" sx={{ p: 1, verticalAlign: 'middle' }}>{event.location}</Box>
                      <Box component="td" sx={{ p: 1, textAlign: 'left', minWidth: 150, whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatEventDateTime(event.date, event.time || '')}</Box>
                      <Box component="td" sx={{ p: 1, textAlign: 'center', verticalAlign: 'middle' }}>
                        {event.website && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleEventWebsiteClick(e, event.id)}
                          >
                            Website
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
          {pageCount > 1 && (
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}
            />
          )}
        </Box>
      </Box>

      {/* Create Event Modal */}
      <CreateEventModal
        open={createEventModalOpen}
        onClose={() => setCreateEventModalOpen(false)}
        onSuccess={() => {
          // Refresh events list after successful creation
          if (refresh) {
            refresh();
          }
        }}
      />
    </>
  );
};

export default EventsList; 